import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { CheckCircle, Clock, Euro, Calendar, MapPin } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const etatColor = (etat) => {
  const e = (etat || "").toLowerCase();
  if (e.includes("bilan recu")) return "bg-green-600 text-white";
  if (e.includes("realise") || e.includes("bilan")) return "bg-amber-500 text-white";
  if (e.includes("place") || e.includes("a venir")) return "bg-blue-600 text-white";
  if (e.includes("attente")) return "bg-orange-400 text-white";
  if (e.includes("propose")) return "bg-gray-400 text-white";
  if (e.includes("refuse")) return "bg-red-500 text-white";
  return "bg-gray-300 text-gray-800";
};
const typeLabel = { evenement: "Événement", placement_direct: "Placement direct", organise: "Organisé" };

export default function SuiviPlanningsPage() {
  const [data, setData] = useState({ compteurs: {}, lignes: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filtreEtat, setFiltreEtat] = useState("");
  const authH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
  const reload = async () => {
    try { const r = await axios.get(`${API}/api/planning-synthese`, authH()); setData(r.data || { compteurs: {}, lignes: [] }); } catch (e) {}
  };
  const reconcilierPaiements = async () => {
    try { await axios.post(`${API}/api/planning-synthese/reconcile-paiements`, {}, authH()); } catch (e) {}
    reload();
  };
  const supprimerUn = async (l) => {
    if (!window.confirm(`Supprimer definitivement "${l.titre}" et ses donnees liees ?`)) return;
    try { await axios.delete(`${API}/api/planning-synthese/ligne/${l.type}/${l.id}`, authH()); reload(); }
    catch (e) { window.alert("Erreur suppression: " + (e?.response?.data?.detail || e.message)); }
  };
  const resetTout = async () => {
    const c = window.prompt("Cela supprime TOUS les plannings et leurs donnees liees. Tapez SUPPRIMER pour confirmer.");
    if (c !== "SUPPRIMER") return;
    try { const r = await axios.delete(`${API}/api/planning-synthese/reset`, authH()); reload(); window.alert("Remise a zero effectuee."); }
    catch (e) { window.alert("Erreur: " + (e?.response?.data?.detail || e.message)); }
  };

  useEffect(() => {
    (async () => {
      try {
        try { await axios.post(`${API}/api/planning-synthese/reconcile-paiements`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }); } catch (e) {}
      const r = await axios.get(`${API}/api/planning-synthese`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setData(r.data || { compteurs: {}, lignes: [] });
      } catch (e) { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const c = data.compteurs || {};
  const etats = Array.from(new Set((data.lignes || []).map((l) => l.etat))).sort();
  const rows = (data.lignes || []).filter((l) => {
    const okQ = !q || (l.titre || "").toLowerCase().includes(q.toLowerCase()) ||
      (l.lieu || "").toLowerCase().includes(q.toLowerCase()) ||
      (l.food_trucks || []).join(" ").toLowerCase().includes(q.toLowerCase());
    const okE = !filtreEtat || l.etat === filtreEtat;
    return okQ && okE;
  });

  const Stat = ({ label, value, icon: Icon }) => (
    <Card><CardContent className="p-4 flex items-center gap-3">
      <Icon className="w-5 h-5 text-secondary" />
      <div><div className="text-2xl font-bold text-secondary">{value ?? 0}</div>
        <div className="text-xs text-gray-500">{label}</div></div>
    </CardContent></Card>
  );

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-secondary">Suivi des plannings</h1>
        <p className="text-sm text-gray-500">Toutes les planifications (événements, organisés, placements directs) et leur état.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Total" value={c.total} icon={Calendar} />
        <Stat label="À venir" value={c.a_venir} icon={Clock} />
        <Stat label="En attente placement" value={c.attente_placement} icon={Clock} />
        <Stat label="À facturer" value={c.a_facturer} icon={Euro} />
        <Stat label="Bilan en attente" value={c.bilan_attente} icon={CheckCircle} />
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Input className="max-w-xs" placeholder="Rechercher (titre, lieu, food truck)" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="border rounded-md h-10 px-3 text-sm" value={filtreEtat} onChange={(e) => setFiltreEtat(e.target.value)}>
          <option value="">Tous les états</option>
          {etats.map((e) => (<option key={e} value={e}>{e}</option>))}
        </select>
        <span className="text-sm text-gray-500">{rows.length} ligne(s)</span>
          <button onClick={resetTout} className="ml-auto bg-red-600 hover:bg-red-700 text-white text-sm px-3 py-2 rounded-md">Remettre a 0 (tout supprimer)</button>
          <button onClick={reconcilierPaiements} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-3 py-2 rounded-md">Rafraichir paiements Stripe</button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Planifications</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (<p className="text-sm text-gray-500">Chargement...</p>) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-3">Titre</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Lieu</th>
                  <th className="py-2 pr-3">Food trucks</th>
                  <th className="py-2 pr-3">État</th>
                  <th className="py-2 pr-3">Montant</th>
                  <th className="py-2 pr-3">Facturé</th>
                      <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((l, i) => (
                  <tr key={i} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-3 font-medium text-secondary">{l.titre}</td>
                    <td className="py-2 pr-3"><Badge variant="outline">{typeLabel[l.type] || l.type}</Badge></td>
                    <td className="py-2 pr-3 whitespace-nowrap"><span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{l.date || "-"}</span></td>
                    <td className="py-2 pr-3">{l.lieu ? (<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{l.lieu}</span>) : "-"}</td>
                    <td className="py-2 pr-3">{l.nb_food_trucks || 0}{l.food_trucks && l.food_trucks.length ? ` · ${l.food_trucks.slice(0,2).join(", ")}${l.food_trucks.length>2?"…":""}` : ""}</td>
                    <td className="py-2 pr-3"><span className={`px-2 py-1 rounded text-xs font-semibold ${etatColor(l.etat)}`}>{l.etat}</span></td>
                    <td className="py-2 pr-3 whitespace-nowrap">{(l.montant || 0)} €</td>
                    <td className="py-2 pr-3">{l.paye ? (<Badge className="bg-green-600">payé</Badge>) : ((l.montant||0)>0 ? (<Badge variant="outline">en attente</Badge>) : (<span className="text-gray-400">—</span>))}</td>
                    <td className="py-2 pr-3"><button onClick={() => supprimerUn(l)} className="text-red-600 hover:underline text-xs">Supprimer</button></td>
                  </tr>
                ))}
                {rows.length === 0 && (<tr><td colSpan={9} className="py-4 text-center text-gray-400">Aucune planification.</td></tr>)}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
